import { Controller } from "@hotwired/stimulus"
import { showProcessingOverlay, hideProcessingOverlay } from "../processing_overlay"

const CLOSING_OVERLAY_ID = "stage-closing-overlay"

// O cliente lê "07/08/2026 14:30", não "2026-08-07T14:30" — o termo tem que
// mostrar a resposta como ela vai ficar registrada.
function formatDateTime(value) {
  const [ date, time ] = value.split("T")
  if (!date || !time) return value

  const [ year, month, day ] = date.split("-")
  return `${day}/${month}/${year} ${time.slice(0, 5)}`
}

// Salva enquanto se digita, mas não a cada letra: há campo de observação com
// frase inteira, e um PATCH por tecla enche o histórico de versões que ninguém
// vai ler — além de castigar quem está com sinal ruim na rua.
const TYPING_DEBOUNCE_MS = 900

const STATUS_PILL = "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm shadow-lg"
const STATUS_TONES = {
  saving: "bg-gray-900/90 text-white",
  success: "bg-gray-900/90 text-white",
  error: "bg-red-600 text-white"
}

// Execução do checklist da etapa: salva resposta a resposta (resiliente a
// sinal ruim em campo), controla visibilidade das perguntas condicionais e
// salva os campos de lacre no blur.
export default class extends Controller {
  static targets = ["question", "status", "termAnswers", "clientName", "clientCpf", "sealWarning", "sealOk"]
  static values = { answerUrl: String, sealUrl: String, collectionUrl: String, paperSignatureUrl: String }

  connect() {
    this.timers = new Map()
    this.onSubmitEnd = (event) => {
      if (!event.detail?.success) {
        hideProcessingOverlay(CLOSING_OVERLAY_ID)
        this.unlockSubmitters()
      }
    }
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.refreshVisibility()
    this.refreshCollection()
    this.updateTermAnswers()
  }

  disconnect() {
    clearTimeout(this.statusTimer)
    this.cancelPending()
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    hideProcessingOverlay(CLOSING_OVERLAY_ID)
  }

  saveAnswer(event) {
    const input = event.target
    if (this.unchanged(input)) return

    this.highlightChoice(input)
    this.refreshVisibility()
    this.updateTermAnswers()
    this.schedule(input, event, () => this.patch(this.answerUrlValue, {
      question_id: input.dataset.questionId,
      value: input.value
    }))
  }

  // Digitando (input): espera a pausa. Escolha ou saída do campo (change,
  // blur): salva na hora — ali a decisão já está tomada.
  schedule(input, event, run) {
    clearTimeout(this.timers.get(input))
    if (event.type !== "input") return this.markSaved(input, run)

    this.timers.set(input, setTimeout(() => this.markSaved(input, run), TYPING_DEBOUNCE_MS))
  }

  // Marca o valor como salvo ANTES de enviar — é o que impede reenviar o mesmo
  // texto a cada saída de campo — e desmarca se o envio falhar, para a próxima
  // tentativa valer.
  markSaved(input, run) {
    const value = input.value
    this.timers.delete(input)
    input.dataset.lastSaved = value
    Promise.resolve(run()).then((ok) => {
      if (ok === false && input.dataset.lastSaved === value) delete input.dataset.lastSaved
    })
  }

  // Passar por um campo sem mexer nele não é alteração: sem isto, cada saída de
  // campo regravava o mesmo valor e piscava "Salvo".
  unchanged(input) {
    return input.dataset.lastSaved !== undefined && input.dataset.lastSaved === input.value
  }

  cancelPending() {
    this.timers.forEach((timer) => clearTimeout(timer))
    this.timers.clear()
  }

  // A moldura verde marca a resposta escolhida. Sem isto ela só acompanhava
  // depois de recarregar: a página ficava dizendo "Sim" com o "Não" marcado.
  highlightChoice(input) {
    if (input.type !== "radio") return

    const question = input.closest("[data-task-checklist-target=question]")
    question?.querySelectorAll("label").forEach((label) => {
      const chosen = label.querySelector("input[type=radio]")?.checked
      label.classList.toggle("border-green-500", chosen)
      label.classList.toggle("bg-green-50", chosen)
      label.classList.toggle("border-gray-300", !chosen)
    })
  }

  saveSeal(event) {
    const input = event.target
    this.checkSeal(input)
    if (this.unchanged(input)) return

    this.schedule(input, event, () =>
      this.patch(this.sealUrlValue, { [input.dataset.sealField]: input.value }))
  }

  // O aviso de divergência responde à digitação: quem corrige o número precisa
  // ver o aviso sumir, senão fica em dúvida se o certo é o que tem em mãos ou o
  // que a tela ainda acusa. Mesma comparação do servidor.
  checkSeal(input) {
    const expected = input.dataset.expectedSeal
    if (!expected || !this.hasSealWarningTarget) return

    const typed = input.value.trim().toLowerCase()
    const matches = typed === expected.trim().toLowerCase()
    this.sealWarningTarget.hidden = typed === "" || matches
    if (this.hasSealOkTarget) this.sealOkTarget.hidden = typed === "" || !matches
  }

  // Quem assinou o termo em papel: grava na hora porque é essa assinatura que
  // dá dono ao arquivo digitalizado — sem ela, o anexo não teria onde entrar.
  // Nome e CPF viajam juntos: a assinatura não existe sem os dois, e mandar só
  // o nome devolveria erro enquanto o CPF ainda não foi digitado.
  savePaperSigner(event) {
    const input = event.target
    if (!this.hasPaperSignatureUrlValue) return

    // A tela pode ter dois blocos de assinatura em papel (a transcrição e a
    // saída "assinou no papel impresso"): lê os campos do bloco editado.
    const scope = input.closest("[data-paper-signer]") || this.element
    const name = scope.querySelector("[name=signer_name]")?.value.trim() || ""
    const cpf = scope.querySelector("[name=signer_cpf]")?.value.trim() || ""
    if (name === "" || cpf === "") return
    if (this.unchanged(input)) return

    this.schedule(input, event, () =>
      this.patch(this.paperSignatureUrlValue, { signer_name: name, signer_cpf: cpf }, "POST"))
  }

  // Como o cliente pagou: salva a escolha e os campos da maquininha juntos, a
  // cada mudança. Não dá baixa — quem dá é o encerramento da etapa.
  saveCollection() {
    if (!this.hasCollectionUrlValue) return
    // A tela responde na hora: os campos da forma escolhida aparecem antes de
    // a resposta do servidor voltar. Sem isso era preciso recarregar a página
    // para ver o campo que a própria escolha acabou de pedir.
    this.refreshCollection()
    this.patch(this.collectionUrlValue, this.collectionParams())
  }

  refreshCollection() {
    const chosen = this.element.querySelector("input[name=collection_kind]:checked")?.value

    this.element.querySelectorAll("[data-collection-panel]").forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.collectionPanel !== chosen)
    })

    this.element.querySelectorAll("[data-collection-pill]").forEach((pill) => {
      const on = pill.dataset.collectionPill === chosen
      pill.classList.toggle("border-green-500", on)
      pill.classList.toggle("bg-green-50", on)
      pill.classList.toggle("border-gray-300", !on)
    })
  }

  collectionParams() {
    const params = {}
    this.element.querySelectorAll("[data-collection-field]").forEach((input) => {
      if (input.type === "radio" && !input.checked) return
      params[input.dataset.collectionField] = input.value
    })
    return params
  }

  // O nome digitado em "Nome de quem assina" substitui o nome no termo em
  // tempo real (quem assina pode não ser quem comprou); vazio volta ao da venda.
  updateClientName(event) {
    this.mirrorInTerm(this.clientNameTargets, event.target.value)
  }

  // Mesma coisa para o CPF — que no termo é o de quem assina, e por isso começa
  // vazio: não existe valor da venda para voltar.
  updateClientCpf(event) {
    this.mirrorInTerm(this.clientCpfTargets, event.target.value)
  }

  mirrorInTerm(targets, value) {
    const typed = value.trim()
    targets.forEach((el) => {
      el.textContent = typed || el.dataset.default || ""
    })
  }

  // Encerrar/assinar dispara o submit ANTES do blur do último campo terminar
  // de salvar (corrida → "checklist incompleto"). Seguramos o submit, salvamos
  // tudo que está na tela e só então reenviamos.
  async flushThenSubmit(event) {
    if (event.defaultPrevented) return // outro handler barrou (ex.: assinatura vazia)
    if (this.flushed) {
      this.flushed = false
      return
    }

    event.preventDefault()
    // Entre o clique e o envio existe o salvamento do que está na tela, e nesse
    // intervalo o botão continuava clicável: quem não vê resposta clica de
    // novo. Trava e diz que está encerrando — o Turbo assume daí em diante.
    this.lockSubmitters(event.target, "Encerrando…")
    showProcessingOverlay({
      id: CLOSING_OVERLAY_ID,
      title: "Encerrando a etapa",
      subtitle: "Guardando o que você preencheu. Não feche esta janela."
    })
    try {
      await this.flushAll()
    } catch {
      this.unlockSubmitters()
      hideProcessingOverlay(CLOSING_OVERLAY_ID)
      throw new Error("falha ao salvar antes de encerrar")
    }
    this.flushed = true
    event.target.requestSubmit()
  }

  // Não usa `disabled`: o botão carrega name/value que o servidor lê (o
  // "finish" da confirmação de dados), e campo desabilitado não é enviado.
  // pointer-events-none barra o segundo clique sem tirar o valor do envio.
  lockSubmitters(form, label) {
    this.locked = Array.from(
      document.querySelectorAll(`[type=submit][form="${form.id}"], button[type=submit][form="${form.id}"]`)
    ).concat(Array.from(form.querySelectorAll("[type=submit], button[type=submit]")))

    this.locked.forEach((el) => {
      el.dataset.busyLabel = el.tagName === "INPUT" ? el.value : el.textContent.trim()
      el.classList.add("opacity-60", "pointer-events-none")
      if (el.tagName === "INPUT") el.value = label
      else el.textContent = label
    })
  }

  unlockSubmitters() {
    (this.locked || []).forEach((el) => {
      el.classList.remove("opacity-60", "pointer-events-none")
      if (el.dataset.busyLabel === undefined) return

      if (el.tagName === "INPUT") el.value = el.dataset.busyLabel
      else el.textContent = el.dataset.busyLabel
      delete el.dataset.busyLabel
    })
    this.locked = []
  }

  async flushAll() {
    // O que estava esperando a pausa da digitação entra aqui pelo valor atual
    // do campo; deixar o timer vivo mandaria o mesmo campo duas vezes.
    this.cancelPending()
    const saves = []

    this.questionTargets.forEach((row) => {
      if (row.classList.contains("hidden")) return
      const radio = row.querySelector("input[type=radio]:checked")
      const field = row.querySelector("textarea, select, input[type=number], input[type=text], input[type=datetime-local]")
      const value = radio ? radio.value : (field ? field.value : "")
      if (value !== "") {
        saves.push(this.patch(this.answerUrlValue, { question_id: row.dataset.questionId, value }))
      }
    })

    this.element.querySelectorAll("[data-seal-field]").forEach((input) => {
      if (input.value !== "") {
        saves.push(this.patch(this.sealUrlValue, { [input.dataset.sealField]: input.value }))
      }
    })

    if (this.hasCollectionUrlValue && this.collectionParams().kind) {
      saves.push(this.patch(this.collectionUrlValue, this.collectionParams()))
    }

    await Promise.all(saves)
  }

  refreshVisibility() {
    const answers = this.currentAnswers()
    this.questionTargets.forEach((row) => {
      const dependsOn = row.dataset.dependsOnId
      if (!dependsOn) return

      const matches = String(answers[dependsOn] ?? "") === row.dataset.dependsOnValue
      row.classList.toggle("hidden", !matches)
    })
  }

  // Espelha as respostas visíveis dentro do visualizador do termo — o cliente
  // lê exatamente o que vai ficar registrado ao assinar/encerrar.
  updateTermAnswers() {
    if (!this.hasTermAnswersTarget) return

    this.termAnswersTarget.replaceChildren()
    this.questionTargets.forEach((row) => {
      if (row.classList.contains("hidden")) return
      // Conferência interna não entra no termo: o cliente não tem que ler nem
      // assinar "endereço confere com o informado no atendimento?".
      if (row.dataset.showInTerm === "false") return

      const radio = row.querySelector("input[type=radio]:checked")
      const field = row.querySelector("textarea, select, input[type=number], input[type=text], input[type=datetime-local]")
      let value = radio ? (radio.value === "true" ? "Sim" : "Não") : (field ? field.value : "")
      if (field?.type === "datetime-local" && value) value = formatDateTime(value)
      if (value === "") value = "—"

      const item = document.createElement("li")
      const prompt = document.createElement("span")
      prompt.className = "text-gray-500"
      prompt.textContent = `${row.dataset.prompt} `
      item.appendChild(prompt)
      item.appendChild(document.createTextNode(value))
      this.termAnswersTarget.appendChild(item)
    })
  }

  currentAnswers() {
    const map = {}
    this.questionTargets.forEach((row) => {
      const id = row.dataset.questionId
      const radio = row.querySelector("input[type=radio]:checked")
      const field = row.querySelector("textarea, select, input[type=number], input[type=text], input[type=datetime-local]")
      map[id] = radio ? radio.value : (field ? field.value : "")
    })
    return map
  }

  async patch(url, body, method = "PATCH") {
    if (!url) return
    this.showStatus("Salvando…")

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
        },
        body: JSON.stringify(body)
      })
      if (response.ok) {
        this.showStatus("Salvo ✓", "success")
      } else {
        this.showStatus("Erro ao salvar — tente novamente", "error")
      }
      return response.ok
    } catch {
      this.showStatus("Sem conexão — tente novamente antes de sair da página", "error")
      return false
    }
  }

  // Pílula flutuante: quem preenche está com o olho no campo, não no topo do
  // cartão. Sucesso some sozinho — erro fica até o próximo salvamento, porque
  // sair da página com dado perdido é o que não pode acontecer.
  showStatus(text, tone = "saving") {
    if (!this.hasStatusTarget) return

    clearTimeout(this.statusTimer)
    const pill = this.statusTarget
    pill.textContent = text
    pill.className = `${STATUS_PILL} ${STATUS_TONES[tone]}`
    pill.hidden = false

    if (tone === "success") {
      this.statusTimer = setTimeout(() => { pill.hidden = true }, 2000)
    }
  }

}
